import castArray from 'lodash-es/castArray';

import { AIRFLOW_PROVIDER } from '../../constants/airflow';
import { SQL_PROVIDER } from '../../constants/sql';
import { Fabric, FabricAspects } from '../../redux/types';
import { getAspectValue } from '../utils/misc/aspect';
import { FabricProviderMap, FabricProviderType } from './types';
import { AirflowFabricPayload, ProphecyManagedAirflowPayload } from './types/airflow';
import { DQProviderInfo, DQ_PROVIDER } from './types/dataQuality';
import { ORCHESTRATION_PROVIDER, OrchestrationProviderInfo } from './types/orchestration';
import { FabricServicePrincipalState, SPARK_PROVIDER, SparkProviderInfo } from './types/spark';
import { SqlBigQueryProvider, SqlProvider, SqlProviderInfo } from './types/sql';

export type ProviderInfoTypes =
  | SparkProviderInfo
  | SqlProvider
  | AirflowFabricPayload
  | DQProviderInfo
  | OrchestrationProviderInfo;

// TODO: Refactor later
export type FabricProviderInfo<P extends ProviderInfoTypes> = P extends SparkProviderInfo
  ? {
      fabricProviderType: FabricProviderType.Spark;
      provider: SPARK_PROVIDER;
      providerInfo: SparkProviderInfo;
      fabricServicePrincipalState?: FabricServicePrincipalState;
    }
  : P extends SqlProvider
    ? {
        fabricProviderType: FabricProviderType.SQL;
        provider: SQL_PROVIDER;
        providerInfo: SqlProvider;
      }
    : P extends AirflowFabricPayload
      ? {
          fabricProviderType: FabricProviderType.Airflow;
          provider: AIRFLOW_PROVIDER;
          providerInfo: AirflowFabricPayload;
        }
      : P extends DQProviderInfo
        ? {
            fabricProviderType: FabricProviderType.DataQuality;
            provider: DQ_PROVIDER;
            providerInfo: DQProviderInfo;
          }
        : P extends OrchestrationProviderInfo
          ? {
              fabricProviderType: FabricProviderType.Orchestration;
              provider: ORCHESTRATION_PROVIDER;
              providerInfo: OrchestrationProviderInfo;
            }
          : never;

// TODO: Remove DQ if unused
export type FabricProviderLanguages = 'SQL' | 'SPARK' | 'JOBS' | 'DQ' | 'ORCHESTRATOR';

export type ComputedFabricProvidersInfo<P extends FabricProviderLanguages> = P extends 'SQL' | 'ORCHESTRATOR'
  ? {
      sqlProviderInfo: SqlProvider | undefined;
      // Orchestrator may or may not exist
      orchestrationProviderInfo: P extends 'ORCHESTRATOR' ? OrchestrationProviderInfo : undefined;
      fabricProviderType: Array<FabricProviderType.SQL | FabricProviderType.Orchestration | FabricProviderType.Spark>;
      // both of them will be same
      provider: SQL_PROVIDER | ORCHESTRATION_PROVIDER;
      // can also have spark provider Info, but the provider will be same in both cases
      sparkProviderInfo?: SparkProviderInfo;

      airflowProviderInfo?: never;
      dqProviderInfo?: never;
    }
  : P extends 'SPARK'
    ? {
        sparkProviderInfo: SparkProviderInfo;
        fabricProviderType: Array<FabricProviderType.Spark | FabricProviderType.SQL | FabricProviderType.Orchestration>;
        provider: SPARK_PROVIDER;
        fabricServicePrincipalState?: FabricServicePrincipalState;
        // can also have sql provider info / orchestration provider info
        // but the provider will be same in both cases
        sqlProviderInfo?: SqlProvider;
        orchestrationProviderInfo?: OrchestrationProviderInfo;

        airflowProviderInfo?: never;
        dqProviderInfo?: never;
      }
    : P extends 'JOBS'
      ? {
          airflowProviderInfo: AirflowFabricPayload;
          fabricProviderType: Array<FabricProviderType.Airflow>;
          provider: AIRFLOW_PROVIDER;

          sqlProviderInfo?: never;
          sparkProviderInfo?: never;
          orchestrationProviderInfo?: never;
          dqProviderInfo?: never;
        }
      : P extends 'DQ'
        ? {
            dqProviderInfo: DQProviderInfo;
            fabricProviderType: Array<FabricProviderType.DataQuality>;
            provider: DQ_PROVIDER;

            sqlProviderInfo?: never;
            sparkProviderInfo?: never;
            orchestrationProviderInfo?: never;
            airflowProviderInfo?: never;
          }
        : never;

function getAllFabricProvidersInfo(fabric: FabricAspects) {
  const fabricServicePrincipalState = getAspectValue<FabricServicePrincipalState>(
    fabric.FabricServicePrincipalStateAspect
  );

  let sparkProviderInfo: FabricProviderInfo<SparkProviderInfo> | undefined,
    sqlProviderInfo: FabricProviderInfo<SqlProvider> | undefined,
    airflowProviderInfo: FabricProviderInfo<AirflowFabricPayload> | undefined,
    dqProviderInfo: FabricProviderInfo<DQProviderInfo> | undefined,
    orchestrationProviderInfo: FabricProviderInfo<OrchestrationProviderInfo> | undefined;

  const sparkProviderInfoValue = getAspectValue<SparkProviderInfo>(
    fabric.SparkProviderInfoAspect || fabric.SparkProviderInfo
  );

  if (sparkProviderInfoValue) {
    const provider =
      sparkProviderInfoValue[sparkProviderInfoValue.providerType as 'databricks']?.cloud === SPARK_PROVIDER.PROPHECY
        ? SPARK_PROVIDER.PROPHECY
        : sparkProviderInfoValue.providerType;
    sparkProviderInfo = {
      fabricProviderType: FabricProviderType.Spark,
      provider: provider as SPARK_PROVIDER,
      providerInfo: sparkProviderInfoValue,
      fabricServicePrincipalState: fabricServicePrincipalState || undefined
    };
  }

  const sqlProviderInfoValue = getAspectValue<SqlProvider>(fabric.SqlProviderInfoAspect);
  if (sqlProviderInfoValue) {
    sqlProviderInfo = {
      fabricProviderType: FabricProviderType.SQL,
      provider: sqlProviderInfoValue.providerInfo.providerType,
      providerInfo: sqlProviderInfoValue
    };
  }

  const airflowProviderInfoValue = getAspectValue<AirflowFabricPayload>(fabric.AirflowProviderInfoAspect);
  if (airflowProviderInfoValue) {
    airflowProviderInfo = {
      fabricProviderType: FabricProviderType.Airflow,
      provider: airflowProviderInfoValue.providerType,
      providerInfo: airflowProviderInfoValue
    };
  }
  const dqProviderInfoValue = getAspectValue<DQProviderInfo>(fabric.DQProviderInfo);
  if (dqProviderInfoValue) {
    dqProviderInfo = {
      fabricProviderType: FabricProviderType.DataQuality,
      provider: dqProviderInfoValue.providerType,
      providerInfo: dqProviderInfoValue
    };
  }

  const orchestrationProviderInfoValue = getAspectValue<OrchestrationProviderInfo>(
    fabric.OrchestrationProviderInfoAspect
  );
  if (orchestrationProviderInfoValue) {
    orchestrationProviderInfo = {
      fabricProviderType: FabricProviderType.Orchestration,
      provider: orchestrationProviderInfoValue.providerType,
      providerInfo: orchestrationProviderInfoValue
    };
  }

  return {
    sparkProviderInfo,
    sqlProviderInfo,
    airflowProviderInfo,
    dqProviderInfo,
    orchestrationProviderInfo
  };
}

export type FabricProviderInfoForMatching = {
  fabricProviderType: Array<FabricProviderType> | FabricProviderType;
  provider: string;
};

export function isFabricProviderMatching(
  fabricProviderInfo: FabricProviderInfoForMatching,
  checkFabricProviderType: FabricProviderType,
  checkProvider?: string
) {
  const { provider, fabricProviderType } = fabricProviderInfo;

  return Boolean(
    castArray(fabricProviderType).includes(checkFabricProviderType) &&
      (checkProvider === undefined || checkProvider === provider)
  );
}

const fabricProviderTypeMatchCache = new Map<string, boolean>();

/**
 * Clears all cached provider type match results for a specific fabric.
 * This removes cache entries from `fabricProviderTypeMatchCache` whose keys start with the given `fabricId`.
 *
 * @param fabricId - The unique identifier of the fabric whose cache entries should be cleared.
 */
export function clearFabricProviderTypeMatchCache(fabricId: string) {
  fabricProviderTypeMatchCache.forEach((_, key) => {
    if (key.startsWith(fabricId)) {
      fabricProviderTypeMatchCache.delete(key);
    }
  });
}

export function isFabricMatching(fabric: Fabric, checkFabricProviderType: FabricProviderType, checkProvider?: string) {
  const cacheKey = `${fabric.id || fabric._id}-${checkFabricProviderType || ''}-${checkProvider}`;
  if (fabricProviderTypeMatchCache.has(cacheKey)) {
    return Boolean(fabricProviderTypeMatchCache.get(cacheKey));
  }
  const fabricProviderInfo = getFabricProviderInfo(fabric);

  const result = Boolean(
    fabricProviderInfo && isFabricProviderMatching(fabricProviderInfo, checkFabricProviderType, checkProvider)
  );

  fabricProviderTypeMatchCache.set(cacheKey, result);
  return result;
}

export function isSparkFabricProvider(
  fabricProviderInfo: ComputedFabricProvidersInfo<FabricProviderLanguages>,
  subProvider?: string
): fabricProviderInfo is ComputedFabricProvidersInfo<'SPARK'> {
  return isFabricProviderMatching(fabricProviderInfo, FabricProviderType.Spark, subProvider);
}

export function isAirflowFabricProvider(
  fabricProviderInfo: ComputedFabricProvidersInfo<FabricProviderLanguages>,
  subProvider?: string
): fabricProviderInfo is ComputedFabricProvidersInfo<'JOBS'> {
  return isFabricProviderMatching(fabricProviderInfo, FabricProviderType.Airflow, subProvider);
}

export function isSQLFabricProvider(
  fabricProviderInfo: ComputedFabricProvidersInfo<FabricProviderLanguages>,
  subProvider?: string
): fabricProviderInfo is ComputedFabricProvidersInfo<'SQL'> {
  return isFabricProviderMatching(fabricProviderInfo, FabricProviderType.SQL, subProvider);
}

export function isSparkFabric(fabric: Fabric, subProvider?: string) {
  return isFabricMatching(fabric, FabricProviderType.Spark, subProvider);
}

export function isOrchestrationFabric(fabric: Fabric, subProvider?: string) {
  return isFabricMatching(fabric, FabricProviderType.Orchestration, subProvider);
}

export function isOrchestrationFabricProvider(
  fabricProviderInfo: ComputedFabricProvidersInfo<FabricProviderLanguages>,
  subProvider?: string
): fabricProviderInfo is ComputedFabricProvidersInfo<'ORCHESTRATOR'> {
  return isFabricProviderMatching(fabricProviderInfo, FabricProviderType.Orchestration, subProvider);
}

export const isSparkDatabricksFabric = (fabric: Fabric) => {
  const providerInfo = getFabricProviderInfo(fabric);
  return providerInfo
    ? isFabricProviderMatching(providerInfo, FabricProviderType.Spark, SPARK_PROVIDER.DATABRICKS) ||
        isFabricProviderMatching(providerInfo, FabricProviderType.Spark, SPARK_PROVIDER.PROPHECY)
    : false;
};

const getUTCDate = (inputDate?: string | number, time?: [number, number, number]) => {
  var date = inputDate ? new Date(inputDate) : new Date();
  return Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    time ? time[0] : date.getUTCHours(),
    time ? time[1] : date.getUTCMinutes(),
    time ? time[2] : date.getUTCSeconds()
  );
};

const oneDayInMs = 1000 * 3600 * 24;
const endOfDay = [23, 59, 59] as [number, number, number];
const startOfDay = [0, 0, 0] as [number, number, number];

export function getFabricExpiryInDays(
  fabric: Fabric,
  fabricProviderInfo: ComputedFabricProvidersInfo<FabricProviderLanguages>
) {
  if (isSparkFabricProvider(fabricProviderInfo) && fabricProviderInfo.provider !== undefined) {
    const { provider, fabricServicePrincipalState } = fabricProviderInfo;
    if (provider !== SPARK_PROVIDER.PROPHECY) return;

    const expiryDate = fabricServicePrincipalState?.expiry;
    const difference = Math.floor(
      (expiryDate
        ? getUTCDate(expiryDate, endOfDay) - getUTCDate()
        : getUTCDate() - getUTCDate(fabric.created, startOfDay)) / oneDayInMs
    );

    return expiryDate ? difference : 21 - difference;
  }

  if (isAirflowFabricProvider(fabricProviderInfo)) {
    const { airflowProviderInfo: providerInfo } = fabricProviderInfo;
    const expiryDate = (providerInfo as ProphecyManagedAirflowPayload).expiry;
    if (!expiryDate) return;

    // Handle never expiry case, expired - created > 5 years
    if (
      Math.floor((getUTCDate(expiryDate, endOfDay) - getUTCDate(fabric.created, startOfDay)) / oneDayInMs) >=
      5 * 365
    ) {
      return;
    }

    const difference = getUTCDate(expiryDate, endOfDay) - getUTCDate();
    return Math.floor(difference / oneDayInMs);
  }
}

export function isFabricExpired(
  fabric: Fabric,
  fabricProviderInfo: ComputedFabricProvidersInfo<FabricProviderLanguages>
) {
  return fabricProviderInfo ? (getFabricExpiryInDays(fabric, fabricProviderInfo) ?? 0) < 0 : false;
}

export const getFabricProviderMapValue = <T>(
  fabricMap: { [K in FabricProviderType]: { [key in FabricProviderMap[K]]: T } },
  providerInfo: ReturnType<typeof getFabricProviderInfo>
) => {
  return (
    providerInfo?.fabricProviderType.length &&
    // use the first fabric provider type
    // since orchestration provider type is same as sql provider type
    (fabricMap[providerInfo.fabricProviderType[0]] as { [key: string]: T })?.[providerInfo.provider]
  );
};

export function isSQLBigQueryProvider(provider: SqlProviderInfo): provider is SqlBigQueryProvider {
  return provider.providerType === SQL_PROVIDER.BigQuery;
}

export const getFabricProviderInfo = (
  fabric: FabricAspects | undefined
): ComputedFabricProvidersInfo<FabricProviderLanguages> | undefined => {
  if (!fabric) {
    return undefined;
  }
  let fabricProviderInfo: ComputedFabricProvidersInfo<FabricProviderLanguages> | undefined;

  const { sparkProviderInfo, sqlProviderInfo, orchestrationProviderInfo, airflowProviderInfo, dqProviderInfo } =
    getAllFabricProvidersInfo(fabric);

  // Helper to collect SQL/Orchestration provider types
  const collectSqlOrchestrationTypes = () => {
    const types: Array<FabricProviderType.SQL | FabricProviderType.Orchestration> = [];

    if (sqlProviderInfo) {
      types.push(FabricProviderType.SQL);
    }
    if (orchestrationProviderInfo) {
      types.push(FabricProviderType.Orchestration);
    }

    return types;
  };

  // Initialize Spark fabric
  if (sparkProviderInfo) {
    fabricProviderInfo = {
      fabricProviderType: [FabricProviderType.Spark],
      sparkProviderInfo: sparkProviderInfo.providerInfo,
      fabricServicePrincipalState: sparkProviderInfo.fabricServicePrincipalState,
      provider: sparkProviderInfo.provider
    } satisfies ComputedFabricProvidersInfo<'SPARK'>;
  }

  // Handle SQL and/or Orchestration providers
  if (sqlProviderInfo || orchestrationProviderInfo) {
    const sqlOrchestrationTypes = collectSqlOrchestrationTypes();
    // Provider is the same across all provider types for a fabric if sql/orchestration/spark info exists
    const provider = (sqlProviderInfo ?? orchestrationProviderInfo)!.provider;

    if (!fabricProviderInfo) {
      // Pure SQL/Orchestration fabric (no Spark)
      fabricProviderInfo = {
        fabricProviderType: sqlOrchestrationTypes,
        provider,
        sqlProviderInfo: sqlProviderInfo?.providerInfo as SqlProvider,
        orchestrationProviderInfo: orchestrationProviderInfo?.providerInfo
      } satisfies ComputedFabricProvidersInfo<'SQL' | 'ORCHESTRATOR'>;
    } else {
      // Multi-provider fabric (Spark + SQL/Orchestration)
      fabricProviderInfo = {
        ...fabricProviderInfo,
        fabricProviderType: [...fabricProviderInfo.fabricProviderType, ...sqlOrchestrationTypes] as Array<
          FabricProviderType.Spark | FabricProviderType.SQL | FabricProviderType.Orchestration
        >,
        sqlProviderInfo: sqlProviderInfo?.providerInfo,
        orchestrationProviderInfo: orchestrationProviderInfo?.providerInfo
      } as ComputedFabricProvidersInfo<'SQL' | 'ORCHESTRATOR' | 'SPARK'>;
    }
  }
  if (airflowProviderInfo) {
    fabricProviderInfo = {
      fabricProviderType: [FabricProviderType.Airflow],
      airflowProviderInfo: airflowProviderInfo.providerInfo,
      provider: airflowProviderInfo.provider
    } satisfies ComputedFabricProvidersInfo<'JOBS'>;
  }
  if (dqProviderInfo) {
    fabricProviderInfo = {
      fabricProviderType: [FabricProviderType.DataQuality],
      dqProviderInfo: dqProviderInfo.providerInfo,
      provider: dqProviderInfo.provider
    } satisfies ComputedFabricProvidersInfo<'DQ'>;
  }

  return fabricProviderInfo;
};

export type ComputedFabricProviderInfo = ReturnType<typeof getFabricProviderInfo>;

export const getSparkProviderInfo = (fabric: FabricAspects) => {
  const fabricProviderInfo = getFabricProviderInfo(fabric);

  if (!fabricProviderInfo || !isSparkFabricProvider(fabricProviderInfo)) {
    return undefined;
  }
  const { provider, sparkProviderInfo } = fabricProviderInfo;

  return {
    fabricProviderType: FabricProviderType.Spark,
    provider,
    providerInfo: sparkProviderInfo
  };
};
